/* eslint-disable no-unused-vars */
import { useMemo, useRef, useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  FormControl,
  Autocomplete,
  DataGrid,
  CurrencyInput,
  Tooltip,
  TextFielDecimalNumbers,
  ListBoxAutocomplete,
  Typography,
  IconButton,
  CircularProgress,
} from "../../../../components";
import { formatPrice, responseErros, unformatPrice } from "../../../../utils";
import { value } from "../../../../helpers";
import { notification, ServicoService } from "../../../../services";
import {
  calcularValorTotalServico,
  calculoDescontoServico,
  recalcularCascataDeValores,
} from "../add-ordem-servico";

const servicoService = new ServicoService();

const OrdemServicoListaServicos = ({
  id,
  ordemServico,
  ordemServicoEntity,
  setOrdemServico,
  servicoList,
  setServicoList,
  prestadoresList,
  removerServicoCadastrado,
  listaOrdemServicoPagamentos,
  listaOrdemServicoServicos,
  setListaOrdemServicoServicos,
  permissaoAlterarValorServico,
  isFechado,
  isCancelado,
}) => {
  const inputServico = useRef();
  const listaServicoRef = useRef([]);
  const [isServicoEdit, setIsServicoEdit] = useState(false);
  const [itensColumnView, setItensColumnView] = useState(false);
  const [servico, setServico] = useState(null);
  const [servicoEditId, setServicoEditId] = useState(null);
  const [loadingAutoCompleteServico, setLoadingAutoCompleteServico] =
    useState(false);

  useEffect(() => {
    listaServicoRef.current = listaOrdemServicoServicos;
  }, [listaOrdemServicoServicos]);

  const colunas = useMemo(() => {
    const cols = [
      {
        field: "codigo",
        headerName: "Código",
        flex: 100,
      },
      {
        field: "descricao",
        headerName: "Serviço",
        flex: 500,
      },
      {
        field: "prestadorServicoId",
        headerName: "P. de Servico",
        flex: 150,
        sortable: false,
        filtroPessoa: true,
        valueGetter: (params) =>
          prestadoresList.find((item) => item.id == params.value)
            ?.nomeRazaoSocial,
      },
      {
        field: "quantidade",
        headerName: "Qtd.",
        flex: 90,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "valor",
        headerName: "Valor (R$)",
        flex: 200,
        headerAlign: "right",
        align: "right",
        valueGetter: (params) => formatPrice(parseFloat(params.value || 0)),
      },
      {
        field: "subtotal",
        headerName: "Subtotal (R$)",
        flex: 200,
        headerAlign: "right",
        align: "right",
        valueGetter: (params) =>
          formatPrice(
            parseFloat(params.row.quantidade) * parseFloat(params.row.valor),
          ),
      },
      {
        field: "desconto",
        headerName: "Desconto (R$)",
        flex: 200,
        headerAlign: "right",
        align: "right",
        valueGetter: (params) => formatPrice(parseFloat(params.value || 0)),
      },
      {
        field: "totalItem",
        headerName: "Total",
        flex: 180,
        headerAlign: "right",
        align: "right",
        valueGetter: (params) => {
          return formatPrice(parseFloat(params.value));
        },
      },
    ];

    if (!(isFechado || isCancelado)) {
      cols.push({
        field: "acoes",
        headerName: "Ações",
        sortable: false,
        flex: 100,
        align: "center",
        headerAlign: "center",
        disableClickEventBubbling: true,
        renderCell: (params) => {
          return (
            <div style={{ display: "flex", gap: "8px" }}>
              <IconButton onClick={() => onClickEditServico(params.id)}>
                <Tooltip title="Editar" arrow placement="right">
                  <i className="ph-fill ph-pencil" style={{ fontSize: 18 }}></i>
                </Tooltip>
              </IconButton>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  onClickRemoverServico(params.id);
                }}
              >
                <Tooltip title="Remover" arrow placement="right">
                  <i className="ph-fill ph-trash" style={{ fontSize: 18 }}></i>
                </Tooltip>
              </IconButton>
            </div>
          );
        },
      });
    }

    return cols;
  }, [id, ordemServico?.status, prestadoresList]);

  const onInputChangeServicos = (event) => {
    if (
      event.key === "Enter" &&
      inputServico.current === document.activeElement
    ) {
      const eventValue = event.target?.value;
      if (eventValue && eventValue.trim().length > 0) {
        setLoadingAutoCompleteServico(true);
        buscarServicos(eventValue);
      }
    }
  };

  const buscarServicos = async (termoBusca) => {
    const filtros = {
      nome: termoBusca,
      descricao: termoBusca,
      codigo: termoBusca,
      ativado: true,
    };

    try {
      const response = await servicoService.getAll(filtros);
      setLoadingAutoCompleteServico(false);

      if (!response.isAxiosError && response.data) {
        setServicoList(response.data.rows);
      } else {
        responseErros(response);
        setServicoList([]);
      }
    } catch (error) {
      setLoadingAutoCompleteServico(false);
      setServicoList([]);
      console.error("Erro ao buscar serviços:", error);
    }
  };

  const limparServico = () => {
    setServico(null);
    setIsServicoEdit(false);
    setServicoEditId(null);
  };

  const handleInputChange = (event) => {
    const { name, value: inputValue } = event.target;
    const servicoAtualizado = {
      ...servico,
      [name]: inputValue,
    };
    setServico(servicoAtualizado);
  };

  const handleInputBlur = (event) => {
    const { name, value } = event.target;
    const valorInserido = parseFloat(unformatPrice(value));
    const servicoAtualizado = recalcularCascataDeValores(
      servico,
      name,
      valorInserido,
    );
    setServico((prevState) => ({ ...prevState, ...servicoAtualizado }));
  };

  const handleInputDesconto = (event) => {
    if (!servico) return;
    const valor = unformatPrice(event.target.value);

    const servicoTemp = {
      ...servico,
      [event.target.name]: valor || 0,
    };

    const subtotal = (servicoTemp.valor || 0) * (servicoTemp.quantidade || 1);

    if (event.target.name === "desconto") {
      if (parseFloat(servicoTemp.desconto) > subtotal) {
        servicoTemp.desconto = subtotal;
      }
      servicoTemp.descontoPorcentagem = calculoDescontoServico(
        servicoTemp,
        false,
      );
    } else {
      servicoTemp.desconto = calculoDescontoServico(servicoTemp);
    }

    servicoTemp.totalItem = subtotal - parseFloat(servicoTemp.desconto || 0);
    setServico(servicoTemp);
  };

  const handleAdicionarServico = (servicoParaAdicionar) => {
    if (!servicoParaAdicionar) return;
    if (!servicoParaAdicionar.servicoId) {
      notification.alertaGenericos("Selecione um serviço");
      return;
    }
    if (!servicoParaAdicionar.prestadorServicoId) {
      notification.alertaGenericos("Selecione um prestador de serviço");
      return;
    }
    if (
      !servicoParaAdicionar.quantidade ||
      servicoParaAdicionar.quantidade <= 0
    ) {
      notification.alertaGenericos("Informe uma quantidade válida");
      return;
    }
    if (
      servicoParaAdicionar.valor === undefined ||
      servicoParaAdicionar.valor === null ||
      servicoParaAdicionar.valor < 0
    ) {
      notification.alertaGenericos("Informe um valor válido");
      return;
    }

    const servicosAtuais = [...listaServicoRef.current];

    if (isServicoEdit && servicoEditId) {
      const index = servicosAtuais.findIndex((s) => s.id === servicoEditId);
      if (index !== -1) {
        servicosAtuais[index] = {
          ...servicoParaAdicionar,
          id: servicoEditId,
        };
      }
    } else {
      const servicoExistente = servicosAtuais.find(
        (s) => s.servicoId === servicoParaAdicionar.servicoId,
      );

      if (servicoExistente) {
        alert(
          "Este serviço já foi adicionado. Para alterar, clique em editar.",
        );
        return;
      }

      servicosAtuais.push({
        ...servicoParaAdicionar,
        id: Date.now().toString(),
      });
    }
    ordemServicoEntity.setChangeServicos(servicosAtuais);
    setOrdemServico({ ...ordemServicoEntity });
    setListaOrdemServicoServicos(servicosAtuais);
    limparServico();

    setTimeout(() => {
      if (inputServico?.current) {
        inputServico.current.focus();
      }
    }, 100);
  };

  const handleChangeServico = (value) => {
    if (!value) return setServico(null);
    const novoServico = {
      servicoId: value.id,
      codigo: value.codigo,
      descricao: value.descricao,
      quantidade: 1,
      valor: value.precoServico || 0,
      desconto: 0,
      descontoPorcentagem: 0,
      totalItem: value.precoServico || 0,
      servicoRecorrente: value.servicoRecorrente ?? false,
      prazoRecorrencia: value.prazoRecorrencia,
      tempoAntecedencia: value.tempoAntecedencia,
      classificacaoServicoNacional:
        value?.classificacaoServicoNacional?.codigoClassificacao,
      codigoServicoNacional: value?.codigoServicoNacional?.codigoNacional,
      aliquotaTotalSimplesNacional: value?.aliquotaTotalSimplesNacional,
    };
    setServico(novoServico);

    setTimeout(() => {
      if (!novoServico.prestadorServicoId) {
        const inputPrestador = document.getElementById("prestadorServicoId");
        if (inputPrestador) inputPrestador.focus();
      } else {
        const botaoAdicionar = document.getElementById(
          "buttonAdicionarServico",
        );
        if (botaoAdicionar) botaoAdicionar.focus();
      }
    }, 100);
  };

  const onClickEditServico = async (idServico) => {
    if (listaOrdemServicoPagamentos?.length > 0) {
      return notification.alertaGenericos(
        "Não é possível editar um serviço que já possui pagamentos cadastrados. Remova os pagamentos para editar o serviço.",
      );
    }
    const item = listaServicoRef.current.find((s) => s.id === idServico);
    if (item) {
      setIsServicoEdit(true);
      setServicoEditId(idServico);

      const servicoParaEditar = {
        servicoId: item.servicoId,
        prestadorServicoId: item.prestadorServicoId,
        codigo: item.codigo,
        descricao: item.descricao,
        quantidade: item.quantidade,
        valor: item.valor,
        desconto: item.desconto || 0,
        descontoPorcentagem: item.descontoPorcentagem || 0,
        totalItem: item.totalItem,
        servicoRecorrente: item.servicoRecorrente ?? false,
        prazoRecorrencia: item.prazoRecorrencia,
        tempoAntecedencia: item.tempoAntecedencia,
        classificacaoServicoNacional: item?.classificacaoServicoNacional,
        codigoServicoNacional: item?.codigoServicoNacional,
        aliquotaTotalSimplesNacional: item?.aliquotaTotalSimplesNacional,
      };

      setServico(servicoParaEditar);
      const servicoCompleto = servicoList.find((s) => s.id === item.servicoId);
      if (!servicoCompleto) {
        try {
          const response = await servicoService.getById(item.servicoId);
          if (!response.isAxiosError) {
            setServicoList([response.data]);
          }
        } catch (error) {
          console.error("Erro ao buscar serviço:", error);
        }
      }
    }
  };

  const handleAddActionsOpen = () => {
    if (!servico) {
      return notification.alertaGenericos("Selecione um serviço");
    }
    setItensColumnView(!itensColumnView);
  };

  const onClickRemoverServico = async (idServico) => {
    const listaAtual = listaServicoRef.current;
    const servico = listaAtual.find((p) => p.id == idServico);
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      `Tem certeza que deseja excluir ${
        servico?.descricao ? `"${servico.descricao}"` : "esse item"
      } da lista?`,
    );
    if (!retornoAlerta.isConfirmed) return;
    const servicosAtuais = listaAtual.filter((s) => s.id !== idServico);
    ordemServicoEntity.setChangeServicos(servicosAtuais);
    setOrdemServico({ ...ordemServicoEntity });
    setListaOrdemServicoServicos(servicosAtuais);
    if (id && typeof idServico === "number")
      removerServicoCadastrado(idServico);

    if (servicoEditId === idServico) {
      limparServico();
    }
  };

  return (
    <>
      <Grid container className="position-relative">
        <Grid
          container
          spacing={2}
          className="mb-2"
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={12}>
            <FormControl variant="outlined" margin="normal" fullWidth>
              <Autocomplete
                ListboxComponent={ListBoxAutocomplete}
                id="servicoId"
                name="servicoId"
                options={servicoList}
                autoHighlight
                loading={loadingAutoCompleteServico}
                loadingText="Carregando"
                noOptionsText="Digite e pressione Enter para buscar"
                renderOption={(props, option) => {
                  let nome = `${option?.codigo} - ${option?.nome}`;
                  return option ? (
                    <li {...props} style={{ background: "transparent" }}>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <div>
                          <span className="fw-bold">{nome}</span>
                          <Typography variant="body2" className="fw-bolder">
                            Descrição: <span>{option?.descricao}</span>
                          </Typography>
                        </div>
                        <div className="text-end">
                          <Typography variant="body2">Preço serviço</Typography>
                          <span className="fs-6">
                            {formatPrice(option?.precoServico || 0)}
                          </span>
                        </div>
                      </div>
                    </li>
                  ) : (
                    ""
                  );
                }}
                getOptionLabel={(option) => {
                  let descricaoServico = `${option?.codigo} - ${option?.descricao}`;
                  return option ? descricaoServico : "";
                }}
                filterSelectedOptions
                onChange={(_, valorSelecionado) =>
                  handleChangeServico(valorSelecionado)
                }
                value={
                  servico
                    ? servicoList.find((s) => s.id === servico.servicoId)
                    : null
                }
                disabled={id && (isFechado || isCancelado || ordemServicoEntity?.status == "CANCELADO")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Buscar serviço"
                    required
                    disabled={id && (isFechado || isCancelado || ordemServicoEntity?.status == "CANCELADO")}
                    onKeyDown={(e) => onInputChangeServicos(e)}
                    inputRef={inputServico}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAutoCompleteServico ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth>
              <Autocomplete
                id="prestadorServicoId"
                name="prestadorServicoId"
                getOptionLabel={(option) => option?.nomeRazaoSocial ?? ""}
                options={prestadoresList}
                autoHighlight
                disabled={!servico?.servicoId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Prestador de Serviço"
                    variant={!servico?.servicoId ? "filled" : "outlined"}
                    disabled={!servico?.servicoId}
                    required
                  />
                )}
                onChange={(e, newValue) => {
                  e.persist();
                  setServico((prevState) => ({
                    ...prevState,
                    prestadorServicoId: newValue?.id,
                  }));

                  setTimeout(() => {
                    const botaoAdicionar = document.getElementById(
                      "buttonAdicionarServico",
                    );
                    if (botaoAdicionar) botaoAdicionar.focus();
                  }, 100);
                }}
                value={value.autoComplete(
                  prestadoresList,
                  servico?.prestadorServicoId,
                )}
              />
            </FormControl>
          </Grid>

          <Grid item xs={6} sm={3}>
            <TextField
              id="quantidade"
              name="quantidade"
              label="Quantidade"
              variant={!servico ? "filled" : "outlined"}
              disabled={!servico}
              InputProps={{ inputProps: { min: 1, step: 1 } }}
              value={value.text(servico?.quantidade)}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              margin="normal"
              type="number"
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={3}>
            <CurrencyInput
              id="valor"
              name="valor"
              label="Valor Serviço R$"
              variant={!servico ? "filled" : "outlined"}
              disabled={!servico || !permissaoAlterarValorServico}
              value={value.text(servico?.valor)}
              onBlur={handleInputBlur}
              fullWidth
            />
          </Grid>

          <Grid item xs={6} sm={4}>
            <CurrencyInput
              id="totalItem"
              name="totalItem"
              label="Total Item R$"
              variant="filled"
              disabled
              value={value.text(servico?.totalItem)}
              fullWidth
            />
          </Grid>

          <Grid
            item
            xs={6}
            sm={2}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 1,
              mt: { xs: 0, sm: "0.5em" },
            }}
          >
            <IconButton
              variant="contained"
              size="small"
              onClick={handleAddActionsOpen}
            >
              <i
                className="ph-bold ph-caret-down"
                style={{
                  color: "#494c62",
                  rotate: itensColumnView ? "180deg" : "0deg",
                }}
              ></i>
            </IconButton>

            <Tooltip
              title={isServicoEdit ? "Atualizar Serviço" : "Adicionar Serviço"}
            >
              <Button
                id="buttonAdicionarServico"
                variant="contained"
                color={isServicoEdit ? "primary" : "success"}
                size="small"
                disabled={!servico}
                sx={{
                  height: "35px",
                  borderRadius: "50%",
                  minWidth: "15px",
                  boxShadow: "none",
                }}
                onClick={() => handleAdicionarServico(servico)}
              >
                <i
                  className={
                    isServicoEdit
                      ? "ph-bold ph-arrows-clockwise"
                      : "ph-bold ph-plus"
                  }
                  style={{ color: "#fff", fontSize: 15 }}
                ></i>
              </Button>
            </Tooltip>
          </Grid>
        </Grid>

        {itensColumnView && (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextFielDecimalNumbers
                id="descontoPorcentagem"
                name="descontoPorcentagem"
                label="Desconto (%)"
                variant="outlined"
                disabled={!servico}
                value={servico?.descontoPorcentagem ?? 0}
                onBlur={handleInputDesconto}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <CurrencyInput
                id="desconto"
                name="desconto"
                label="Desconto (R$)"
                variant="outlined"
                disabled={!servico}
                value={servico?.desconto ?? 0}
                onBlur={handleInputDesconto}
                fullWidth
              />
            </Grid>
          </Grid>
        )}

        <Grid container spacing={3}>
          <Grid style={{ marginTop: 32 }} item xs={12}>
            <DataGrid
              style={{ zIndex: 1 }}
              rows={listaOrdemServicoServicos}
              rowCount={listaOrdemServicoServicos.length}
              columns={colunas}
              disableAcoes
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default OrdemServicoListaServicos;
